import {z} from 'zod'

import {tzDateFromIso} from 'src/api/zod-helpers'

export const employmentClassificationSchema = z.enum(['w2_employee', '1099_contractor'])
export type EmploymentClassification = z.infer<typeof employmentClassificationSchema>

export const employmentClassificationLabels: Record<EmploymentClassification, string> = {
    w2_employee: 'W-2 employee',
    '1099_contractor': '1099 contractor',
}

export const employmentClassificationOptions = Object.entries(employmentClassificationLabels).map(
    ([value, label]) => ({value: value as EmploymentClassification, label}),
)

export function employmentClassificationLabel(value: EmploymentClassification | null) {
    return value == null ? 'Not classified' : employmentClassificationLabels[value]
}

const workerRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    worker_type_id: z.string(),
    employment_classification: employmentClassificationSchema.nullable().default(null),
    archived_at: z.string().nullable().default(null),
    home_dispatch_yard_id: z.string().nullable().default(null),
    is_placeholder: z.boolean().default(false),
})

export const workerSchema = workerRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    workerTypeId: raw.worker_type_id,
    employmentClassification: raw.employment_classification,
    homeDispatchYardId: raw.home_dispatch_yard_id,
    isPlaceholder: raw.is_placeholder,
    archivedAt: raw.archived_at == null ? null : tzDateFromIso.parse(raw.archived_at),
}))

export type WorkerRaw = z.input<typeof workerRawSchema>
export type Worker = z.infer<typeof workerSchema>

const workerDetailRawSchema = workerRawSchema.extend({
    worker_type_name: z.string(),
    employment_classification: employmentClassificationSchema.nullable().default(null),
    email: z.string().nullable().default(null),
    phone: z.string().nullable().default(null),
})

export const workerDetailSchema = workerDetailRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    workerTypeId: raw.worker_type_id,
    workerTypeName: raw.worker_type_name,
    employmentClassification: raw.employment_classification,
    email: raw.email,
    phone: raw.phone,
    homeDispatchYardId: raw.home_dispatch_yard_id,
    isPlaceholder: raw.is_placeholder,
    archivedAt: raw.archived_at == null ? null : tzDateFromIso.parse(raw.archived_at),
}))

export type WorkerDetailRaw = z.input<typeof workerDetailRawSchema>
export type WorkerDetail = z.infer<typeof workerDetailSchema>

export const createWorkerInputSchema = z.object({
    name: z.string().transform((value) => value.trim()),
    workerTypeId: z.string(),
    employmentClassification: employmentClassificationSchema,
    email: z
        .string()
        .optional()
        .transform((value) => value?.trim() || null),
    phone: z
        .string()
        .optional()
        .transform((value) => value?.trim() || null),
    homeDispatchYardId: z.string().nullable().optional(),
    isPlaceholder: z.boolean().optional(),
})

export type CreateWorkerInput = z.input<typeof createWorkerInputSchema>

export const createWorkerRequestSchema = createWorkerInputSchema.transform(
    ({
        name,
        workerTypeId,
        employmentClassification,
        email,
        phone,
        homeDispatchYardId,
        isPlaceholder,
    }) => ({
        name,
        worker_type_id: workerTypeId,
        employment_classification: employmentClassification,
        email,
        phone,
        ...(homeDispatchYardId !== undefined ? {home_dispatch_yard_id: homeDispatchYardId} : {}),
        ...(isPlaceholder !== undefined ? {is_placeholder: isPlaceholder} : {}),
    }),
)

export type CreateWorkerRequestRaw = z.infer<typeof createWorkerRequestSchema>

export const updateWorkerInputSchema = createWorkerInputSchema

export type UpdateWorkerInput = z.input<typeof updateWorkerInputSchema>

export const updateWorkerRequestSchema = createWorkerRequestSchema

export type UpdateWorkerRequestRaw = z.infer<typeof updateWorkerRequestSchema>

const workerAssignmentRawSchema = z.object({
    id: z.string(),
    job_id: z.string(),
    job_name: z.string(),
    start_date: z.string(),
    start_time: z.string().nullable(),
    end_date: z.string().nullable(),
    end_time: z.string().nullable(),
})

export const workerAssignmentSchema = workerAssignmentRawSchema.transform((raw) => ({
    id: raw.id,
    jobId: raw.job_id,
    jobName: raw.job_name,
    startDate: raw.start_date,
    startTime: raw.start_time,
    endDate: raw.end_date,
    endTime: raw.end_time,
}))

export type WorkerAssignmentRaw = z.infer<typeof workerAssignmentRawSchema>
export type WorkerAssignment = z.infer<typeof workerAssignmentSchema>

export const workerAssignmentListRawSchema = z.object({
    assignments: z.array(workerAssignmentRawSchema),
})

export const workerAssignmentListSchema = workerAssignmentListRawSchema.transform((raw) => ({
    assignments: raw.assignments.map((assignment) => workerAssignmentSchema.parse(assignment)),
}))

export type WorkerAssignmentListRaw = z.infer<typeof workerAssignmentListRawSchema>
export type WorkerAssignmentList = z.infer<typeof workerAssignmentListSchema>

export type ListWorkerAssignmentsOptions = {
    dateFrom: string
    dateTo: string
}

const workerTimeEntryRawSchema = z.object({
    id: z.string(),
    job_id: z.string(),
    job_name: z.string(),
    start_date: z.string(),
    start_time: z.string(),
    end_date: z.string(),
    end_time: z.string(),
    duration_hours: z.number(),
    created_at: z.string(),
})

export const workerTimeEntrySchema = workerTimeEntryRawSchema.transform((raw) => ({
    id: raw.id,
    jobId: raw.job_id,
    jobName: raw.job_name,
    startDate: raw.start_date,
    startTime: raw.start_time,
    endDate: raw.end_date,
    endTime: raw.end_time,
    durationHours: raw.duration_hours,
    createdAt: tzDateFromIso.parse(raw.created_at),
}))

export type WorkerTimeEntryRaw = z.infer<typeof workerTimeEntryRawSchema>
export type WorkerTimeEntry = z.infer<typeof workerTimeEntrySchema>

export const workerTimeEntryListRawSchema = z.object({
    entries: z.array(workerTimeEntryRawSchema),
})

export const workerTimeEntryListSchema = workerTimeEntryListRawSchema.transform((raw) => ({
    entries: raw.entries.map((entry) => workerTimeEntrySchema.parse(entry)),
}))

export type WorkerTimeEntryListRaw = z.infer<typeof workerTimeEntryListRawSchema>
export type WorkerTimeEntryList = z.infer<typeof workerTimeEntryListSchema>

export type ListWorkerTimeEntriesOptions = {
    limit?: number
}

export type ListWorkersOptions = {
    workerTypeId?: string
    includeArchived?: boolean
}

const workerImportPreviewRowRawSchema = z.object({
    row_index: z.number(),
    name: z.string(),
    worker_type: z.string(),
    employment_classification: z.string().nullable().default(null),
    email: z.string().nullable().default(null),
    phone: z.string().nullable().default(null),
    status: z.enum(['valid', 'invalid']),
    error: z.string().nullable().default(null),
    warning: z.string().nullable().default(null),
})

export const workerImportPreviewRowSchema = workerImportPreviewRowRawSchema.transform((raw) => ({
    rowIndex: raw.row_index,
    name: raw.name,
    workerType: raw.worker_type,
    employmentClassification: raw.employment_classification,
    email: raw.email,
    phone: raw.phone,
    status: raw.status,
    error: raw.error,
    warning: raw.warning,
}))

export type WorkerImportPreviewRowRaw = z.infer<typeof workerImportPreviewRowRawSchema>
export type WorkerImportPreviewRow = z.infer<typeof workerImportPreviewRowSchema>

const workerImportPreviewRawSchema = z.object({
    current_template_version: z.string(),
    template_outdated: z.boolean(),
    warning: z.string().nullable().default(null),
    rows: z.array(workerImportPreviewRowRawSchema),
})

export const workerImportPreviewSchema = workerImportPreviewRawSchema.transform((raw) => ({
    currentTemplateVersion: raw.current_template_version,
    templateOutdated: raw.template_outdated,
    warning: raw.warning,
    rows: raw.rows.map((row) => workerImportPreviewRowSchema.parse(row)),
}))

export type WorkerImportPreviewRaw = z.infer<typeof workerImportPreviewRawSchema>
export type WorkerImportPreview = z.infer<typeof workerImportPreviewSchema>

const workerImportErrorRawSchema = z.object({
    row_index: z.number(),
    message: z.string(),
})

export const workerImportErrorSchema = workerImportErrorRawSchema.transform((raw) => ({
    rowIndex: raw.row_index,
    message: raw.message,
}))

export type WorkerImportErrorRaw = z.infer<typeof workerImportErrorRawSchema>
export type WorkerImportError = z.infer<typeof workerImportErrorSchema>

const workerImportResultRawSchema = z.object({
    imported_count: z.number(),
    skipped_count: z.number(),
    current_template_version: z.string(),
    template_outdated: z.boolean(),
    warning: z.string().nullable().default(null),
    errors: z.array(workerImportErrorRawSchema),
})

export const workerImportResultSchema = workerImportResultRawSchema.transform((raw) => ({
    importedCount: raw.imported_count,
    skippedCount: raw.skipped_count,
    currentTemplateVersion: raw.current_template_version,
    templateOutdated: raw.template_outdated,
    warning: raw.warning,
    errors: raw.errors.map((error) => workerImportErrorSchema.parse(error)),
}))

export type WorkerImportResultRaw = z.infer<typeof workerImportResultRawSchema>
export type WorkerImportResult = z.infer<typeof workerImportResultSchema>
